"use client";

import { useEffect, useRef, useState } from "react";
import { useLocale } from "next-intl";
import { Accordion, Anchor, Badge, Box, Button, Group, NativeSelect, Paper, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import { Link } from "@/i18n/navigation";
import { PRICING } from "@/constants/pricing";
import { KIBBE_TYPES, LEGACY_KIBBE_TYPES, kibbeName } from "@/lib/kibbe";
import { trackKibbe } from "@/lib/kibbeClient";
import { kibbeGrowthCopy, kibbeOfferHref, kibbeOfferMetadata, type KibbeOfferContext } from "@/lib/kibbeGrowthCopy";
import womenswear from "@/public/kibbe-samples/womenswear/report.json";
import menswear from "@/public/kibbe-samples/menswear/report.json";
import styles from "./KibbeOffer.module.css";

export default function KibbeOffer({ showSample = true, ...context }: KibbeOfferContext & { showSample?: boolean }) {
  const locale = useLocale();
  const copy = kibbeGrowthCopy[locale as keyof typeof kibbeGrowthCopy] || kibbeGrowthCopy.en;
  const [known, setKnown] = useState(kibbeName(context.type) || "");
  const element = useRef<HTMLDivElement>(null);
  const shown = useRef(false);
  const metadata = kibbeOfferMetadata(context);
  const { source, placement, content, type, type_b } = metadata;
  useEffect(() => {
    if (!element.current || shown.current) return;
    const observer = new IntersectionObserver(entries => {
      if (!shown.current && entries.some(entry => entry.isIntersecting)) {
        shown.current = true;
        trackKibbe("offer_shown", { source, placement, content, ...(type ? { type } : {}), ...(type_b ? { type_b } : {}) });
        observer.disconnect();
      }
    }, { threshold: 0.15 });
    observer.observe(element.current);
    return () => observer.disconnect();
  }, [source, placement, content, type, type_b]);
  const click = (action: "analysis" | "styling" | "sample") => trackKibbe("offer_click", { ...metadata, action });
  const example = context.clothing === "menswear" ? menswear : womenswear;
  const mode = context.clothing === "menswear" ? "menswear" : "womenswear";
  const heading = context.source === "color_result" ? copy.colorTitle : context.source === "style_quiz_result" ? copy.quizTitle : context.source === "menswear_guide" ? copy.mensTitle : kibbeName(context.type) && kibbeName(context.otherType) ? copy.compareTitle : kibbeName(context.type) ? copy.typeTitle : copy.title;
  const title = heading.replace("{type}", kibbeName(context.type) || "").replace("{other}", kibbeName(context.otherType) || "");
  const buttonStyles = { root: { height: "auto", minHeight: 44, paddingTop: 12, paddingBottom: 12 }, label: { whiteSpace: "normal" as const } };
  return <Paper ref={element} component="section" aria-label={title} p={{ base: "lg", md: 32 }} className={styles.offer}>
    <SimpleGrid cols={{ base: 1, md: showSample ? 2 : 1 }} spacing={{ base: "xl", md: 40 }}>
      <Stack gap="md" justify="center">
        <Box><Badge color="grape.8" variant="light" mb="md">{copy.badge}</Badge><Title order={2} fz={{ base: 27, md: 34 }} className={styles.title}>{title}</Title></Box>
        {context.source === "style_quiz_result" && <Text size="sm">{copy.quizNote}</Text>}
        <Text lh={1.7} className={styles.body}>{context.source === "color_result" ? copy.colorDetail : copy.detail}</Text>
        <Text fw={700} size="lg">{(context.source === "color_result" ? copy.colorPrice : copy.price).replace("{price}", PRICING.KIBBE_ANALYSIS.BASE_PRICE.toFixed(2)).replace("{color}", String(PRICING.UPSELLS.COLOR_ANALYSIS_ADDON.BASE_PRICE))}</Text>
        <Text size="sm" className={styles.body}>{copy.estimate}</Text>
        <Group><Button component={Link} href={kibbeOfferHref(context)} color="grape.8" size="md" onClick={() => click("analysis")} styles={buttonStyles}>{copy.cta}</Button></Group>
        <Accordion variant="contained" styles={{ item: { background: "white" } }}>
          <Accordion.Item value="known-type"><Accordion.Control>{copy.known}</Accordion.Control><Accordion.Panel>
            <Text size="sm" c="dimmed" mb="md">{copy.knownHelp}</Text>
            <Stack gap="md"><NativeSelect label={copy.choose} value={known} onChange={event => setKnown(event.target.value)} data={[{ value: "", label: copy.choose }, ...KIBBE_TYPES, ...LEGACY_KIBBE_TYPES]} />
              <Button component={Link} href={kibbeOfferHref(context, "styling", known)} disabled={!known} variant="outline" color="grape.8" onClick={() => click("styling")} styles={buttonStyles}>{copy.knownCta}</Button>
            </Stack>
          </Accordion.Panel></Accordion.Item>
        </Accordion>
        {!showSample && <Anchor component={Link} href={kibbeOfferHref(context, "sample")} onClick={() => click("sample")}>{copy.sample} →</Anchor>}
      </Stack>
      {showSample && <Paper p="lg" className={styles.proof}>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <img src={`/kibbe-samples/${mode}/look-1.webp`} alt={example.result.wardrobePlan.outfits[0].name} width={512} height={768} loading="lazy" className={styles.look} />
          <Stack gap="md" justify="center"><Text fw={700} size="lg">{copy.sampleType.replace("{type}", example.result.bodyType)}</Text><Text size="sm" lh={1.7} className={styles.body}>{example.result.evidence[0].observation}</Text><Text size="sm" fw={600}>{example.result.wardrobePlan.outfits[0].name}</Text><Anchor component={Link} href={kibbeOfferHref(context, "sample")} onClick={() => click("sample")} c="grape.8" fw={600}>{copy.sample} →</Anchor></Stack>
        </SimpleGrid>
        <Text size="xs" className={styles.body} mt="md">{copy.sampleNote}</Text>
      </Paper>}
    </SimpleGrid>
  </Paper>;
}
